import React, { useContext } from "react";
import GlobalPopUp from "../../../components/utility/globalPopUp";
import GlobalDeletePopup from "../../../components/utility/globalDeletePopup/globalDeletePopup";
import AssignAgentPopup from "../threadView/components/agentPopup/assignAgentPopup";
import RemoveAgentPopup from "../threadView/components/agentPopup/removeAgentPopup";
import FeedbackDialog from "../threadView/components/chatWidgetComponents/feedbackDialog";
import AssignThreadTagPopup from "../threadInfo/components/threadTag/assignThreadTagPopup";
import CreateUpdateThreadTagPopup from "../threadInfo/components/threadTag/createUpdateThreadTagPopup";
import SendAgentActivityMsgConfirmation from "../threadView/components/sendAgentActivityMsgConfirmation";
import AddNewThreadPopup from "../threadList/addNewThreadPopup";
import { useRuleBasedInboxStore } from "../../../store/ruleBasedInboxStore";
import { UserContext, UserContextPayload } from "../../../context/userContext";
import { useGenericStore } from "../../../store/genericStore";
import { useShallow } from "zustand/react/shallow";
import agent from "../../../services/api";
import { currentActiveChatbotThreadTypeNew, newThreadInfo, threadTagsData } from "../../../models/axiosModel";
import useGenericHook from "../../../hooks/useGenericHook";

interface InboxModalsProps {
    flagSendAgentActivityMsg: boolean;
    handleToggleManualOverride: (
        user_assignment_id: string,
        flag_delete_agent: boolean,
        flag_send_agent_activity_message: boolean
    ) => Promise<void>;
    getManualOverrideConfig: (thread: currentActiveChatbotThreadTypeNew | null) => string;
    getSpecificChat: (
        chatbot_user_id: number | string,
        offSetValue: number,
        flag_debug: boolean,
        flagViewDetailedLog: boolean,
        flagGetThreadDetailByPhoneNumber?: boolean,
        phoneNumberInfo?: newThreadInfo | null,
        forceRefresh?: boolean
    ) => Promise<void>;
}

/**
 * Owns inbox popup store subscriptions so toggling modals does not re-render
 * the thread list / chat view tree in RuleBasedInbox.
 */
const InboxModals: React.FC<InboxModalsProps> = ({
    flagSendAgentActivityMsg,
    handleToggleManualOverride,
    getManualOverrideConfig,
    getSpecificChat,
}) => {
    const { isScreenBelow1000 } = useGenericHook();
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { showSuccess } = useGenericStore(
        useShallow((state) => ({
            showSuccess: state.showSuccess,
        }))
    );

    const {
        flagAssignAgentPopup,
        setFlagAssignAgentPopup,
        flagRemoveAgentPopup,
        setFlagRemoveAgentPopup,
        isFeedbackFormOpen,
        setIsFeedbackFormOpen,
        flagAssignThreadTagPopup,
        setFlagAssignThreadTagPopup,
        flagUpdateThreadTagPopup,
        setFlagUpdateThreadTagPopup,
        flagDeleteThreadTagConfirmation,
        setFlagDeleteThreadTagConfirmation,
        flagSendAgentActivityMsgConfirmation,
        setFlagSendAgentActivityMsgConfirmation,
        selectedThreadTag,
        getThreadTagApi,
        flagNewThreadPopup,
        setFlagNewThreadPopup
    } = useRuleBasedInboxStore(
        useShallow((state) => ({
            flagAssignAgentPopup: state.flagAssignAgentPopup,
            setFlagAssignAgentPopup: state.setFlagAssignAgentPopup,
            flagRemoveAgentPopup: state.flagRemoveAgentPopup,
            setFlagRemoveAgentPopup: state.setFlagRemoveAgentPopup,
            isFeedbackFormOpen: state.isFeedbackFormOpen,
            setIsFeedbackFormOpen: state.setIsFeedbackFormOpen,
            flagAssignThreadTagPopup: state.flagAssignThreadTagPopup,
            setFlagAssignThreadTagPopup: state.setFlagAssignThreadTagPopup,
            flagUpdateThreadTagPopup: state.flagUpdateThreadTagPopup,
            setFlagUpdateThreadTagPopup: state.setFlagUpdateThreadTagPopup,
            flagDeleteThreadTagConfirmation: state.flagDeleteThreadTagConfirmation,
            setFlagDeleteThreadTagConfirmation: state.setFlagDeleteThreadTagConfirmation,
            flagSendAgentActivityMsgConfirmation: state.flagSendAgentActivityMsgConfirmation,
            setFlagSendAgentActivityMsgConfirmation: state.setFlagSendAgentActivityMsgConfirmation,
            selectedThreadTag: state.selectedThreadTag,
            getThreadTagApi: state.getThreadTagApi,
            flagNewThreadPopup: state.flagNewThreadPopup,
            setFlagNewThreadPopup: state.setFlagNewThreadPopup,
        }))
    );

    const deleteThreadTags = async (tag: threadTagsData | null) => {
        if (!tag) return;

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const res = await agent.deleteThreadTags.delete(tag.ticket_tag_id);

        if (!res.status) {
            handleError(res);
        } else {
            getThreadTagApi();
            showSuccess("Tag Deleted Successfully.");
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    };

    return (
        <>
            <GlobalPopUp
                open={flagAssignAgentPopup}
                handleClose={() => setFlagAssignAgentPopup(false)}
                flagBackdrop
                flagShowCloseIcon={true}
                titlePadding="0px"
                paperMinWidth={isScreenBelow1000 ? "310px" : "700px"}
                paperMaxwidth={isScreenBelow1000 ? "610px" : "750px"}
            >
                <AssignAgentPopup
                    handleToggleManualOverride={handleToggleManualOverride}
                    flagSendAgentActivityMsg={flagSendAgentActivityMsg}
                />
            </GlobalPopUp>

            <GlobalPopUp
                open={flagRemoveAgentPopup}
                handleClose={() => setFlagRemoveAgentPopup(false)}
                flagBackdrop
                flagShowCloseIcon={true}
                titlePadding="0px"
                paperMinWidth={isScreenBelow1000 ? "310px" : "700px"}
                paperMaxwidth={isScreenBelow1000 ? "610px" : "750px"}
            >
                <RemoveAgentPopup
                    handleToggleManualOverride={handleToggleManualOverride}
                    flagSendAgentActivityMsg={flagSendAgentActivityMsg}
                />
            </GlobalPopUp>

            <GlobalPopUp
                titlePadding="0px !important"
                open={isFeedbackFormOpen}
                handleClose={() => setIsFeedbackFormOpen(false)}
                overflow="hidden"
            >
                <FeedbackDialog onCloseOfFeedbackForm={() => setIsFeedbackFormOpen(false)} feedbackFor="message" />
            </GlobalPopUp>

            {flagAssignThreadTagPopup && (
                <GlobalPopUp
                    open={flagAssignThreadTagPopup}
                    handleClose={() => setFlagAssignThreadTagPopup(false)}
                    onClose={() => setFlagAssignThreadTagPopup(false)}
                    flagShowCloseIcon={true}
                    titlePadding="0px"
                    paperMinWidth={isScreenBelow1000 ? "310px" : "700px"}
                    paperMaxwidth={isScreenBelow1000 ? "610px" : "750px"}
                >
                    <AssignThreadTagPopup />
                </GlobalPopUp>
            )}

            {flagUpdateThreadTagPopup && (
                <GlobalPopUp
                    open={flagUpdateThreadTagPopup}
                    handleClose={() => setFlagUpdateThreadTagPopup(false)}
                    onClose={() => setFlagUpdateThreadTagPopup(false)}
                    flagShowCloseIcon={true}
                    titlePadding="0px"
                    paperMinWidth={isScreenBelow1000 ? "310px" : "700px"}
                    paperMaxwidth={isScreenBelow1000 ? "610px" : "750px"}
                >
                    <CreateUpdateThreadTagPopup />
                </GlobalPopUp>
            )}

            {flagSendAgentActivityMsgConfirmation && (
                <GlobalPopUp
                    open={flagSendAgentActivityMsgConfirmation}
                    handleClose={() => setFlagSendAgentActivityMsgConfirmation(false)}
                    onClose={() => setFlagSendAgentActivityMsgConfirmation(false)}
                    flagShowCloseIcon={true}
                    titlePadding="0px"
                    paperMinWidth={isScreenBelow1000 ? "310px" : "400px"}
                    paperMaxwidth={isScreenBelow1000 ? "610px" : "500px"}
                >
                    <SendAgentActivityMsgConfirmation
                        getManualOverrideConfig={getManualOverrideConfig}
                        handleToggleManualOverride={handleToggleManualOverride}
                    />
                </GlobalPopUp>
            )}

            {flagDeleteThreadTagConfirmation && (
                <GlobalDeletePopup
                    deleteDescription={`Are you sure you want to delete ${selectedThreadTag?.ticket_tag_name} tag`}
                    open={flagDeleteThreadTagConfirmation}
                    onClose={() => setFlagDeleteThreadTagConfirmation(false)}
                    onDelete={() => {
                        deleteThreadTags(selectedThreadTag);
                        setFlagDeleteThreadTagConfirmation(false);
                    }}
                />
            )}

            {flagNewThreadPopup && (
                <GlobalPopUp
                    open={flagNewThreadPopup}
                    handleClose={() => setFlagNewThreadPopup(false)}
                    onClose={() => setFlagNewThreadPopup(false)}
                    flagShowCloseIcon={true}
                    titlePadding="0px"
                    paperMinWidth={isScreenBelow1000 ? "310px" : "700px"}
                    paperMaxwidth={isScreenBelow1000 ? "610px" : "750px"}
                >
                    <AddNewThreadPopup
                        getSpecificChat={getSpecificChat}
                    />
                </GlobalPopUp>
            )}
        </>
    );
};

export default InboxModals;
